import StatusCode from "../util/StatusCode"
import { CoreAlvisImportConfiguration } from "./CoreAlvisModels";
import { CoreAppToken } from "./CoreAppToken"
import { CoreAppleSchoolManagerConfiguration } from "./CoreAsmModels"
import { CoreSchoolSoftConfiguration } from "./CoreSchoolSoftConfigurationModels"
import { CoreFlowSyncCsvImport } from "./CoreFlowSyncCsvModels";
import { CoreAzureAdGroupExport } from "./CoreAzureAdGroupModels";
import { CoreDnpExportConfiguration } from "./CoreDnpModels";
import { CoreEdlevoConfiguration } from "./CoreEdlevoModels";
import { CoreEduCloudConfiguration } from "./CoreEduCloudModels";
import { CoreEntraIdUserExportConfiguration } from "./CoreEntraIdUserModels";
import { CoreGoogleClassroomConfiguration } from "./CoreGoogleClassroomModels";
import { CoreGoogleDeviceImportConfiguration } from "./CoreGoogleDeviceImportModels";
import { CoreGoogleGroupConfiguration } from "./CoreGoogleGroupModels";
import { CoreGoogleUserExportConfiguration } from "../util/GoogleUserExportTypes";
import { CoreHaldorExportConfiguration } from "./CoreHaldorModels";
import { CoreLocalADConfiguration } from "./CoreLocalAdModels";
import { CoreLocalADScheduleConfiguration } from "./CoreLocalAdScheduleModels";
import { CoreMeitnerExportConfiguration } from "./CoreMeitnerModels";
import { CoreOneRosterConfiguration } from "./CoreOneRosterConfigurationModels";
import { CoreScheduleSyncGoogleConfiguration } from "./CoreScheduleSyncGoogleModels";
import { CoreSchoolSoftScheduleConfiguration } from "./CoreSchoolSoftScheduleConfigurationModels";
import { CoreSDSv21CsvImportConfiguration } from "./CoreSDSv21CsvImportModels";
import { CoreSkola24ScheduleConfiguration } from "./CoreSkola24ScheduleConfigurationModels";
import { CoreSS12000ApiConfiguration } from "./CoreSS12000ApiConfiguration";
import { CorePasswordDistributionApiConfiguration } from "./CorePasswordDistributionApiConfiguration";

// --- Enums ---
export enum ModuleType {
    Undefined = 0,
    ImportModule = 1,
    ExportModule = 2,
}

export enum ModuleInstanceStatus {
    Undefined = 0,
    Started = 1,
    Completed = 2,
    FinishedWithError = 3,
    FinishedInQuarantine = 4,
    Cancelled = 5,
}
export enum ModuleInstanceStatusSeverity {
    Undefined = 0,
    Critical = 1,
    Error = 2,
    Warning = 3,
    Ok = 4,
}
export enum CoreUserRoleType {
    Unknown = 0,
    Student = 1,
    Duty = 2,
    UserRelation = 3,
}

export enum CoreTriggerType {
    Undefined = 0,
    CronSchedule = 1,
    RunAfterModule = 2
}

export enum AppTokenType {
    UNDEFINED = 0,
    MICROSOFT_APPLICATION = 1,
    MICROSOFT_DELEGATED = 2,
    GOOGLE_SERVICE_ACCOUNT = 3
}

export enum CoreModuleIconState {
    NONE = 0,
    OK = 1,
    WARNING = 2,
    ERROR = 3,
    CRITICAL = 4,
}

export enum CoreSelectableTriggerState {
    UNDEFINED = 0,
    SELECTABLE = 1,
    UNSELECTABLE_SELF = 2,
    Unselectable_LOOP = 3,
}

export enum FlowSyncProduct {
    UNDEFINED = 0,
    SCHOOL_ADMIN = 1,
    SCHEDULE_SYNC = 2,
}

export enum FlowSyncObjectType {
    Undefined = 0,
    Group = 1,
    Activity = 2,
    Device = 3,
    Classroom = 4,
}

/// --- Interfaces ---
export interface CoreModule {
    id: string
    active: boolean
    dryRun: boolean
    moduleTypeName: string
    moduleTypeDisplayName: string
    title: string
    nextScheduledStart?: string
    status?: CoreModuleStatus
    moduleTriggers?: CoreModuleTrigger[]
    moduleType: ModuleType
    appToken?: CoreAppToken
    runnable: boolean
    dryRunEnabled: boolean
    fileDownloadEnabled: boolean
    selectSchoolUnits: boolean
    sourceEnvironment: string
    product?: FlowSyncProduct
    displayGracePeriod: boolean
}

export interface CoreModuleView {
    active: boolean
    dryRun: boolean
    id: string
    moduleTriggers?: CoreModuleTrigger[]
    moduleType: ModuleType
    moduleTypeName: ModuleTypeName
    nextScheduledStart?: string
    sourceEnvironment: string
    status?: CoreModuleStatus
    title?: string
    runnable: boolean
    selectSchoolUnits: boolean
    moduleTypeDisplayName?: string
    fileDownloadEnabled: boolean
    dryRunEnabled: boolean
    importSchoolUnits: CoreImportModuleSchoolUnit[]
    exportSchoolUnits: CoreExportModuleSchoolUnit[]
    appToken?: CoreAppToken
    configuration: CoreAlvisImportConfiguration
    | CoreAppleSchoolManagerConfiguration 
    | CoreAzureAdGroupExport 
    | CoreDnpExportConfiguration 
    | CoreEdlevoConfiguration  
    | CoreEduCloudConfiguration  
    | CoreEntraIdUserExportConfiguration 
    | CoreFlowSyncCsvImport 
    | CoreGoogleClassroomConfiguration 
    | CoreGoogleDeviceImportConfiguration
    | CoreGoogleGroupConfiguration 
    | CoreGoogleUserExportConfiguration 
    | CoreHaldorExportConfiguration 
    | CoreLocalADConfiguration 
    | CoreLocalADScheduleConfiguration 
    | CoreMeitnerExportConfiguration 
    | CoreOneRosterConfiguration 
    | CoreScheduleSyncGoogleConfiguration 
    | CoreSchoolSoftConfiguration 
    | CoreSchoolSoftScheduleConfiguration 
    | CoreSDSv21CsvImportConfiguration 
    | CoreSkola24ScheduleConfiguration 
    | CoreSS12000ApiConfiguration
    | CorePasswordDistributionApiConfiguration
}

export enum ModuleTypeName {
    TietoEducation = "TietoEducation",
    SchoolSoft = "SchoolSoft",
    AzureADImport = "AzureADImport",
    LocalAD = "LocalAD",
    SchoolSoftSchedule = "SchoolSoftSchedule",
    Skola24Schedule = "Skola24Schedule",
    LocalADSchedule = "LocalADSchedule",
    EduCloud = "EduCloud",
    OneRosterConfiguration = "OneRosterConfiguration",
    GoogleClassroomAdmin = "GoogleClassroomAdmin",
    GoogleClassroomExport = "GoogleClassroomExport",
    GoogleDeviceImport = "GoogleDeviceImport",
    ScheduleSyncGoogle = "ScheduleSyncGoogle",
    AzureADGroupExport = "AzureADGroupExport",
    EntraIdUserExport = "EntraIdUserExport", 
    GoogleGroupExport = "GoogleGroupExport",
    HaldorExport = "HaldorExport",
    SS12000API = "SS12000API",
    PasswordDistributionAPI = "PasswordDistributionAPI",
    ScheduleSyncExchange = "ScheduleSyncExchange",
    GoogleUserExport = "GoogleUserExport",
    AppleSchoolManager = "AppleSchoolManager",
    DNPExport = "DNPExport",
    SDSv21CsvImport = "SDSv21CsvImport",
    AlvisImport = "AlvisImport",
    FlowSyncCsvImport = "FlowSyncCsvImport",
    MeitnerExport = "MeitnerExport",
    FlowSyncChangePassword = "FlowSyncChangePassword",
    GoogleDeviceExport = "GoogleDeviceExport",
}

export interface CoreExportModuleSchoolUnit {
    id: string
    isActive: boolean
    title?: string
    shouldImport: boolean
    schoolUnitCode?: string
}

export interface CoreImportModuleSchoolUnit {
    id: string
    title?: string
    shouldImport: boolean
    isActive: boolean
    exportModules?: CoreImportModuleSchoolUnitExportModule[]
}

export interface CoreImportModuleSchoolUnitExportModule {
    id: string
    title: string
}

export interface CoreModuleStatus {
    id: string
    isStartRequested: boolean
    startedAt?: string
    completedAt?: string
    isInQuarantine: boolean
    isActive: boolean
    title?: string
    runnable: boolean
    fileDownloadEnabled: boolean
    selectSchoolUnits: boolean
    iconState: CoreModuleIconState
    runningTrailingModule?: CoreModuleRef
    status: ModuleInstanceStatus,
    latestActivityAt: string
}

export interface CoreModuleRef {
    id: string
    title?: string
}

export interface CoreModuleTrigger {
    id?: string
    triggerType: number
    cronSchedule?: string
    runAfterModuleId?: string
    runAfterModuleTitle?: string
}

export interface CoreModuleInstanceListing {
    id: string
    moduleId: string
    startedAt: string
    completedAt?: string
    isFinishedWithError: boolean
    isFinishedInQuarantine: boolean
    countWarning: number
    countError: number
    countCritical: number
}

export interface CoreModuleInstance {
    moduleInstanceId: string
    moduleId: string
    title: string
    startedAt: string
    completedAt?: string
}

export interface CoreModuleInstancePhase {
    id: string
    moduleInstanceId: string
    phaseName: string
    startedAt: string
    completedAt?: string
    status: ModuleInstanceStatus
    severity: ModuleInstanceStatusSeverity
    modifiedAt: string
    moduleInstancePhaseStats: CoreModuleInstancePhaseStat[]
}

export interface CoreModuleInstancePhaseStat {
    id: string
    moduleInstancePhaseId: string
    objectType: string
    total: number
    handled: number
    failed: number
    analyzed?: number
    created?: number
    updated?: number
    inactivated?: number
    archived?: number
    deleted?: number
}

export interface CoreModuleTriggerOverviewModule {
    id: string,
    title: string,
    active: boolean,
    moduleType: ModuleType
}

export interface CoreModuleTriggerOverviewTrigger {
    id: string,
    moduleId: string,
    cronSchedule: string,
    runAfterModuleId: string,
    triggerType: CoreTriggerType,
    nextScheduledStart: string
}

export interface CoreModuleTriggerOverview {
    modules: CoreModuleTriggerOverviewModule[],
    triggers: CoreModuleTriggerOverviewTrigger[],
}

export interface CoreSelectableTriggerModule {
    id: string
    moduleTypeName?: string
    title?: string
    selectable: CoreSelectableTriggerState
}

export interface CoreModuleInstanceLogEntry {
    eventId: number
    severity: number
    message?: string
    eventName?: string
    objectId?: string
    objectSource?: string
    objectType?: string
    timestamp?: string
    module?: CoreModuleRef
    arguments?: { [key: string]: string }
}

export interface CoreMyModuleTypes {
    schoolAdmin: boolean
    scheduleSync: boolean
    oneRoster: boolean
    deviceManager: boolean
    licensedModuleTypeNames: ModuleTypeName[]
    classroomAdminClassroom: boolean
}

export interface RoleFilter {
    id: string
    type: number
    displayName: string
}

export interface CoreGoogleDeviceFilter {
    orgUnitPaths: string[]
    statuses: number[]
    osVersions: string[]
}

export enum CoreClassroomAdminClassroomFilterStatus {
    ORPHAN_RISK = 1,
    ORPHANED = 2,
    ACTIVE = 3,
    ARCHIVED = 4,
}

export enum CoreClassroomAdminClassroomSyncState {
    SYNCHED = 1,
    MANUAL = 2,
}

export interface CoreClassroomAdminClassroomFilter {
    schoolUnits: CoreDisplayableRef[]
    statuses: CoreClassroomAdminClassroomFilterStatus[]
    syncStates: CoreClassroomAdminClassroomSyncState[]
}

export interface Result<T> {
    code: StatusCode
    data: T
    error: Error
}

export interface PaginationResult<T> extends Result<T> {
    pagination: {
        totalPages: number
        currentPage: number
        pageSize: number
    }
    totalCount?: number
}

export interface NoContentResult {
    code: StatusCode
    error: Error
}

export interface CoreEnumeration {
    id: number
    name: string
}

export interface CoreDisplayableRef {
    id: string
    title?: string
    active: boolean
}

export interface PageResult<T> {
    totalCount: number
    currentPage: number
    totalPages: number
    pageSize: number
    values: T[]
}

export interface CoreFilter {
    title: string
}

export interface CoreSyllabusFilter {
    id: string
    subjectName?: string
    subjectDesignation?: string
    courseCode?: string,
    courseName?: string,
    startSchoolYear?: number,
    endSchoolYear?: number
}

export enum StringValueBuilderPartValueSource {
    UNKNOWN = 0,

    // Hämta förnamn från person-objektet i FlowSync
    PERSON_FIRST_NAME = 1,

    // Hämta efternamn från person-objektet i FlowSync
    PERSON_LAST_NAME = 2,

    // Slumpa fram mellan MinLength och MaxLength antal bokstäver från ValueText
    RANDOM_FROM_CHARSET = 3,

    // Årskurs (Endast applicerbart på elever)
    STUDENT_SCHOOL_YEAR = 4,

    // Startår (Om vi passerat sista juni är detta nuvarande år annars nästkommande år)
    PERIOD_AWARE_START_YEAR = 5,

    // Slutår (Om vi passerat sista juni är detta nästkommande år annars innevarande år)
    PERIOD_AWARE_END_YEAR = 6,

    // Konstant värde från ValueText
    CONSTANT = 7,

    // Födelseår kort format (två siffror ex 25)
    YEAR_OF_BIRTH = 8,
    /// <summary>
    /// Hämta klass från studentGroup objectet typ class. 
    /// </summary>
    CLASS = 9,

    /// <summary>
    /// För grundskola är stadie F-3, 4-6 och 7-9
    /// </summary>
    STAGE = 10,

    /// <summary>
    /// Utbildningsform
    /// </summary>
    FORMOFEDUCATION = 11,

    /// <summary>
    /// Primär skola
    /// </summary>
    PRIMARYSCHOOL = 12,
}

export interface CoreStringValueBuilderPart {
    valueSource: StringValueBuilderPartValueSource
    valueText?: string
    minLength?: number
    maxLength?: number
}

export interface CoreStopPeriod {
    id: string
    moduleId: string
    reason: string
    startDayOfMonth: number
    startMonth: number
    stopDayOfMonth: number
    stopMonth: number
    preventNextModule: boolean
}

export interface CoreGracePeriod {
    id: string
    moduleId: string
    forceArchiveGraceDays?: number 
    forceDeleteGraceDays?: number 
    startDayOfMonth: number
    startMonth: number
    stopDayOfMonth: number
    stopMonth: number
}

export interface CoreFlowSyncModuleInfo {
    id: string
    vendorLogo: string
    vendorName: string
    moduleTypeName: string
    moduleType: ModuleType
    isReleased: boolean
    hasLicense: boolean
}

export interface CoreCreateModule {
    title?: string
    moduleType?: string
}